/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { getGroupsData, setMaxHeightForRefs, isPbPreview } from '../../layout-utils';
import { Loader } from '../../atoms/loader';
import { LeftArrowIcon, RightArrowIcon } from '../loginless-common/icons';
import { SectionHeader } from '../loginless-common/section-header';
import { LoginlessLayout } from '../loginless-common/loginless-layout';
import { GroupCard } from '../../molecules/group-card';
import './loginless-grouped.m.css';

export const LoginlessGrouped = (props) => {
  const subscriptionConfig = get(props, ['config', 'pagebuilder-config', 'subscription', 'pageSetting'], {});
  const isPB = isPbPreview(props);

  const groups = getGroupsData(props.currentPath, props.subscriptions) || [];
  const { planSelectionHeading = 'Select Your Plan', showOfferPrice = false } = subscriptionConfig;

  // Selected plan per group: { [groupId]: planObject }
  const [selectedSubscriptions, setSelectedSubscriptions] = useState(() => {
    const initial = {};
    groups.forEach((g) => {
      if (g.id && g.subscription_plans?.length) {
        initial[g.id] = g.subscription_plans[0];
      }
    });
    return initial;
  });

  // Selected plan across all groups (the exact plan that will be purchased)
  const defaultPlan = groups[0]?.subscription_plans?.[0] || null;
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan);
  const activePlan = selectedPlan || defaultPlan;

  // Dropdown overlay open/closed tracking
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const canScrollLeft = currentCardIndex > 0;
  const canScrollRight = currentCardIndex < groups.length - 1;

  const toggleDropdown = (id) => {
    if (activeDropdown !== id) {
      setActiveDropdown(id);
    } else {
      setActiveDropdown(null);
    }
  };

  // Close dropdown on outside click without requiring a blocking full-screen backdrop
  useEffect(() => {
    if (!activeDropdown) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest || !e.target.closest('[data-dropdown-wrapper]')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [activeDropdown]);

  // Sync state if groups arrive dynamically
  useEffect(() => {
    if (groups.length > 0) {
      setSelectedSubscriptions((prev) => {
        const updated = { ...prev };
        let hasChanges = false;
        groups.forEach((g) => {
          if (g.id && !updated[g.id] && g.subscription_plans?.length) {
            updated[g.id] = g.subscription_plans[0];
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });

      setSelectedPlan((prev) => {
        if (!prev && groups[0]?.subscription_plans?.[0]) {
          return groups[0].subscription_plans[0];
        }
        return prev;
      });
    }
  }, [groups]);

  const listRef = useRef(null);
  const cardRefs = useRef([]);

  cardRefs.current = cardRefs.current.slice(0, groups.length);

  useEffect(() => {
    const syncHeights = () => {
      if (cardRefs.current?.length) {
        cardRefs.current.forEach((el) => {
          if (el) el.style.height = 'auto';
        });
        const validCardRefs = cardRefs.current.filter(Boolean).map((el) => ({ current: el }));
        if (validCardRefs.length) {
          setMaxHeightForRefs(validCardRefs);
        }
      }
    };

    syncHeights();
    const rafId = requestAnimationFrame(syncHeights);
    window.addEventListener('resize', syncHeights);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', syncHeights);
    };
  }, [groups, selectedSubscriptions]);

  // Sync currentCardIndex when groups change (e.g. dynamic load) to stay in bounds
  useEffect(() => {
    if (groups.length > 0) {
      setCurrentCardIndex((prev) => Math.min(prev, groups.length - 1));
    }
  }, [groups.length]);

  const scrollToCard = (index) => {
    const cardEl = cardRefs.current[index];
    if (cardEl) {
      cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    } else if (listRef.current) {
      const cardWidth = listRef.current.firstElementChild?.getBoundingClientRect()?.width || 240;
      listRef.current.scrollTo({ left: index * (cardWidth + 16), behavior: 'smooth' });
    }
  };

  const handlePrev = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newIndex = Math.max(0, currentCardIndex - 1);
    setCurrentCardIndex(newIndex);
    const newPlan = groups[newIndex]?.subscription_plans?.[0];
    if (newPlan) setSelectedPlan(newPlan);
    scrollToCard(newIndex);
  };

  const handleNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newIndex = Math.min(groups.length - 1, currentCardIndex + 1);
    setCurrentCardIndex(newIndex);
    const newPlan = groups[newIndex]?.subscription_plans?.[0];
    if (newPlan) setSelectedPlan(newPlan);
    scrollToCard(newIndex);
  };

  if (props?.isAccessTypeUserSetLoading) {
    return <Loader data={groups} subscriptionConfig={subscriptionConfig} />;
  }

  const enableDarkMode = get(props, ['enableDarkMode'], false);
  const mode = enableDarkMode ? 'dark' : 'light';

  const activeGroup =
    groups.find(
      (g) => g.id === activePlan?.subscription_group_id || g.subscription_plans?.some((p) => p.id === activePlan?.id)
    ) || groups[0] || {};
  const isSingleGroup = groups.length === 1;
  const hasFewGroups = groups.length <= 2;
  const groupsListStyle = `groups-list ${hasFewGroups ? 'has-few-groups' : ''} ${isSingleGroup ? 'has-single-group' : ''}`.trim();
  const dynamicGroupDescription = activeGroup?.description || activePlan?.description;
  const isDark = mode === 'dark';
  const arrowPrevDesktopStyle = `arrow-btn arrow-prev desktop-only ${isDark ? 'dark' : ''}`.trim();
  const arrowNextDesktopStyle = `arrow-btn arrow-next desktop-only ${isDark ? 'dark' : ''}`.trim();
  const arrowPrevMobileStyle = `arrow-btn arrow-prev ${isDark ? 'dark' : ''}`.trim();
  const arrowNextMobileStyle = `arrow-btn arrow-next ${isDark ? 'dark' : ''}`.trim();

  return (
    <LoginlessLayout
      subscriptionConfig={subscriptionConfig}
      mode={mode}
      isPB={isPB}
      activePlan={activePlan}
      description={dynamicGroupDescription}
      descriptionType="group"
      config={props?.config}
      localizedTime={props?.localizedTime}
      initLoginlessSubscription={props?.initLoginlessSubscription}
      navigateTo={props?.navigateTo}
      member={props?.member}
    >
      <div styleName="groups-container">
        <SectionHeader title={planSelectionHeading || "Select Your Plan"} mode={mode} />
        <div styleName="carousel-wrapper">
          {groups.length > 2 && (
            <button
              styleName={arrowPrevDesktopStyle}
              type="button"
              aria-label="Previous"
              onClick={handlePrev}
              disabled={!canScrollLeft}
              data-test-id="subscription-plan-prev-btn"
            >
              <LeftArrowIcon mode={mode} />
            </button>
          )}
          <div styleName={groupsListStyle} ref={listRef}>
            {groups.map((group, index) => (
              <GroupCard
                key={group.id}
                group={group}
                isSingleCard={isSingleGroup}
                selectedPlan={activePlan}
                onSelect={(plan) => {
                  setSelectedPlan(plan);
                  setCurrentCardIndex(index);
                }}
                selectedSubscriptions={selectedSubscriptions}
                setSelectedSubscriptions={setSelectedSubscriptions}
                activeDropdown={activeDropdown}
                toggleDropdown={toggleDropdown}
                mode={mode}
                localizedTime={props?.localizedTime}
                showOfferPrice={showOfferPrice}
                cardRef={(el) => {
                  cardRefs.current[index] = el;
                }}
              />
            ))}
          </div>
          {groups.length > 2 && (
            <button
              styleName={arrowNextDesktopStyle}
              type="button"
              aria-label="Next"
              onClick={handleNext}
              disabled={!canScrollRight}
              data-test-id="subscription-plan-next-btn"
            >
              <RightArrowIcon mode={mode} />
            </button>
          )}
        </div>
        {groups.length > 1 && (
          <div styleName="arrows-mobile-container">
            <button
              styleName={arrowPrevMobileStyle}
              type="button"
              aria-label="Previous"
              onClick={handlePrev}
              disabled={!canScrollLeft}
              data-test-id="subscription-plan-prev-btn-mobile"
            >
              <LeftArrowIcon mode={mode} />
            </button>
            <button
              styleName={arrowNextMobileStyle}
              type="button"
              aria-label="Next"
              onClick={handleNext}
              disabled={!canScrollRight}
              data-test-id="subscription-plan-next-btn-mobile"
            >
              <RightArrowIcon mode={mode} />
            </button>
          </div>
        )}
      </div>
    </LoginlessLayout>
  );
};

LoginlessGrouped.propTypes = {
  subscriptions: PropTypes.array,
  currentPath: PropTypes.string,
  config: PropTypes.object,
  isPB: PropTypes.bool,
  isPbPreview: PropTypes.bool,
  defaultSubscriptionHandler: PropTypes.func,
  isAccessTypeUserSetLoading: PropTypes.bool,
  enableDarkMode: PropTypes.bool,
  localizedTime: PropTypes.string,
  initLoginlessSubscription: PropTypes.func,
  navigateTo: PropTypes.func,
  member: PropTypes.object
};

